/**
 * DashboardPage — main monitoring dashboard (replaces Unit 1 stub).
 *
 * Orchestrates:
 *   - Polling interval (usePollingInterval)
 *   - URL filter state (useFilterParams)
 *   - Stack data (useStacks)
 *   - Dashboard status / stale detection (useDashboardStatus)
 *   - Alerts (useAlerts, useAcknowledgeAlert, useDismissAlert)
 *
 * Renders:
 *   - FilterBar with derived Application/Environment options
 *   - PollingIndicator
 *   - AlertBanner
 *   - View mode toggle (Stack View / Resource Grid)
 *   - StackView or ResourceGrid based on viewMode
 *
 * Error handling: Pattern 8.2 — shows stale data with error card, then retry.
 * Default view: Stack View (BR-U5-07).
 */
import { useState, useMemo } from 'react';
import { usePollingInterval } from '@/hooks/usePollingInterval';
import { useFilterParams } from '@/hooks/useFilterParams';
import { useStacks } from '@/api/resourcesApi';
import { useDashboardStatus } from '@/api/resourcesApi';
import { useAlerts, useAcknowledgeAlert, useDismissAlert } from '@/api/alertsApi';
import { FilterBar } from '@/components/FilterBar';
import { PollingIndicator } from '@/components/PollingIndicator';
import { AlertBanner } from '@/components/AlertBanner';
import { StackView } from '@/components/StackView';
import { ResourceGrid } from '@/components/ResourceGrid';
import styles from './DashboardPage.module.css';

type ViewMode = 'stacks' | 'resources';

export function DashboardPage() {
  const [pollingInterval] = usePollingInterval();
  const [filters, setFilters] = useFilterParams();
  const [viewMode, setViewMode] = useState<ViewMode>('stacks');

  // Data queries
  const {
    data: stacks,
    error: stacksError,
    isLoading: stacksLoading,
    dataUpdatedAt: stacksUpdatedAt,
    refetch: refetchStacks,
  } = useStacks(filters, pollingInterval);

  const {
    data: status,
  } = useDashboardStatus(pollingInterval);

  const {
    data: alerts,
    error: alertsError,
  } = useAlerts(pollingInterval);

  const acknowledge = useAcknowledgeAlert();
  const dismiss = useDismissAlert();

  // Derive filter options from all stacks (unfiltered base query piggybacks the cache)
  const { data: allStacks } = useStacks({ application: null, environment: null }, pollingInterval);

  const applications = useMemo((): string[] => {
    if (!allStacks) return [];
    return [...new Set(allStacks.map((s) => s.applicationName))].sort();
  }, [allStacks]);

  const environments = useMemo((): string[] => {
    if (!allStacks) return [];
    return [...new Set(allStacks.map((s) => s.environment))].sort();
  }, [allStacks]);

  // Stale data detection
  const isStale = status?.accounts.some((a) => a.stale) ?? false;

  // Alert action tracking sets
  const acknowledgePendingIds = new Set<number>(
    acknowledge.variables !== undefined && acknowledge.isPending ? [acknowledge.variables as number] : [],
  );
  const dismissPendingIds = new Set<number>(
    dismiss.variables !== undefined && dismiss.isPending ? [dismiss.variables as number] : [],
  );
  const acknowledgeErrorIds = new Set<number>(
    acknowledge.variables !== undefined && acknowledge.isError ? [acknowledge.variables as number] : [],
  );
  const dismissErrorIds = new Set<number>(
    dismiss.variables !== undefined && dismiss.isError ? [dismiss.variables as number] : [],
  );

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <FilterBar
          filters={filters}
          onFilterChange={setFilters}
          applications={applications}
          environments={environments}
        />
        <PollingIndicator
          dataUpdatedAt={stacksUpdatedAt}
          isStale={isStale}
        />
      </div>

      {alertsError && (
        <div role="alert" className={styles.errorCard}>
          <p>Unable to load alerts. {(alertsError as Error).message}</p>
        </div>
      )}

      {alerts && (
        <AlertBanner
          alerts={alerts}
          onAcknowledge={(id) => acknowledge.mutate(id)}
          onDismiss={(id) => dismiss.mutate(id)}
          acknowledgePendingIds={acknowledgePendingIds}
          dismissPendingIds={dismissPendingIds}
          acknowledgeErrorIds={acknowledgeErrorIds}
          dismissErrorIds={dismissErrorIds}
        />
      )}

      <div className={styles.viewControls}>
        <div className={styles.viewToggle} role="group" aria-label="View mode">
          <button
            type="button"
            className={`${styles.viewButton} ${viewMode === 'stacks' ? styles.viewButtonActive : ''}`}
            onClick={() => setViewMode('stacks')}
            aria-pressed={viewMode === 'stacks'}
          >
            Stack View
          </button>
          <button
            type="button"
            className={`${styles.viewButton} ${viewMode === 'resources' ? styles.viewButtonActive : ''}`}
            onClick={() => setViewMode('resources')}
            aria-pressed={viewMode === 'resources'}
          >
            Resource Grid
          </button>
        </div>

        {status && (
          <div className={styles.summary} aria-label="Resource counts">
            <span>EC2: {status.totalEc2}</span>
            <span>RDS: {status.totalRds}</span>
            <span>ECS: {status.totalEcs}</span>
          </div>
        )}
      </div>

      {stacksError && stacks && (
        <div role="status" className={styles.staleCard}>
          ⚠ Showing cached data — refresh failed
        </div>
      )}

      {stacksError && !stacks && (
        <div role="alert" className={styles.errorCard}>
          <p>Unable to load stacks. {(stacksError as Error).message}</p>
          <button
            type="button"
            className={styles.retryButton}
            onClick={() => void refetchStacks()}
          >
            Retry
          </button>
        </div>
      )}

      {viewMode === 'stacks' ? (
        <StackView stacks={stacks ?? []} isLoading={stacksLoading && !stacks} />
      ) : (
        <ResourceGrid stacks={stacks ?? []} isLoading={stacksLoading && !stacks} />
      )}
    </div>
  );
}
